import React from 'react';
import { Box, Typography, Button, Container, Grid } from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DevicesIcon from '@mui/icons-material/Devices';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import heroImg from '../../../images/re_listing_hero.png';

const stats = [
  { icon: <HomeWorkIcon sx={{ color: '#4338ca', fontSize: 22 }} />, label: 'Properties Listed', value: '10,000+' },
  { icon: <TrendingUpIcon sx={{ color: '#059669', fontSize: 22 }} />, label: 'Leads Generated', value: '2.5 Lakh+' },
  { icon: <DoneAllIcon sx={{ color: '#f59e0b', fontSize: 22 }} />, label: 'Deals Closed', value: '8,500+' },
  { icon: <DevicesIcon sx={{ color: '#6366f1', fontSize: 22 }} />, label: 'Active Portals', value: '500+' },
];

const Hero = () => (
  <Box sx={{
    minHeight: '88vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    bgcolor: '#ffffff',
    py: { xs: 10, md: 5 },
  }}>
    {/* Soft gradient background blobs — NO harsh bottom shadow */}
    <Box sx={{ position: 'absolute', top: -200, right: -200, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 65%)', pointerEvents: 'none' }} />
    <Box sx={{ position: 'absolute', bottom: -150, left: -150, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(67,56,202,0.05) 0%, transparent 65%)', pointerEvents: 'none' }} />

    {/* Subtle dot grid overlay */}
    <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(99,102,241,0.12) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none', opacity: 0.6 }} />

    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Grid container spacing={8} alignItems="center">
        {/* Left: Text content */}
        <Grid item xs={12} md={6}>
          {/* Pill badge */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '40px', px: 2.5, py: 1, mb: 4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4f46e5', animation: 'pulse 1.5s infinite' }} />
            <Typography sx={{ color: '#4338ca', fontSize: 13, fontWeight: 700, letterSpacing: 1, fontFamily: "'Montserrat', sans-serif" }}>YOUR OWN REAL ESTATE PORTAL</Typography>
          </Box>

          <Typography variant="h1" sx={{ color: '#0f172a', fontWeight: 900, fontSize: { xs: '2.2rem', md: '3.6rem' }, lineHeight: 1.1, mb: 3, fontFamily: "'Montserrat', sans-serif" }}>
            Help Buyers Find Their{' '}
            <Box component="span" sx={{ color: '#4338ca' }}>Dream Property</Box>
            {' '}on <Box component="span" sx={{ color: '#6366f1', fontStyle: 'italic' }}>Your</Box> Platform
          </Typography>

          <Typography sx={{ color: '#475569', fontSize: 17, mb: 5, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.8, maxWidth: 480 }}>
            Launch a branded, SEO-powered property listing website. Showcase your inventory, capture qualified leads and compete with the big portals — with your logo and your domain.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 5 }}>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} size="large"
              sx={{ bgcolor: '#4338ca', color: '#fff', px: 5, py: 1.7, borderRadius: '14px', fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 16, boxShadow: '0 10px 30px rgba(67,56,202,0.3)', '&:hover': { bgcolor: '#3730a3', transform: 'translateY(-2px)' }, transition: 'all 0.3s' }}>
              Get Free Demo
            </Button>
            <Button variant="outlined" size="large"
              sx={{ borderColor: '#c7d2fe', color: '#4338ca', bgcolor: '#ffffff', px: 5, py: 1.7, borderRadius: '14px', fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 16, '&:hover': { borderColor: '#4338ca', bgcolor: '#eef2ff' }, transition: 'all 0.3s' }}>
              View Features
            </Button>
          </Box>

          {/* Stats row */}
          <Grid container spacing={2}>
            {stats.map((s, i) => (
              <Grid item xs={6} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#0f172a', fontWeight: 900, fontSize: '1.05rem', fontFamily: "'Montserrat', sans-serif", lineHeight: 1 }}>{s.value}</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: 11.5, fontFamily: "'Montserrat', sans-serif", mt: 0.3 }}>{s.label}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Right: Screenshot mockup in clean card frame */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative' }}>
            {/* Decorative blob behind image */}
            <Box sx={{ position: 'absolute', inset: '10% -5%', borderRadius: '40px', background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', zIndex: 0, transform: 'rotate(-3deg)' }} />

            {/* Browser chrome mockup frame */}
            <Box sx={{ position: 'relative', zIndex: 1, borderRadius: '20px', bgcolor: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 30px 70px rgba(99,102,241,0.12)', overflow: 'hidden' }}>
              {/* Fake browser bar */}
              <Box sx={{ bgcolor: '#f1f5f9', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Box sx={{ flex: 1, bgcolor: '#ffffff', borderRadius: '6px', px: 2, py: 0.5, ml: 1, border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: 11, fontFamily: 'monospace' }}>yourportal.com/properties</Typography>
                </Box>
              </Box>
              <img src={heroImg} alt="Real Estate Listing Portal" style={{ width: '100%', display: 'block' }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default Hero;
